/**
 * Recipe Core - recipe lookup with input scaling applied. Pure, no DOM.
 *
 * Extracted from crafting.js so it can be used headlessly. crafting.js carries
 * the crafting UI (infinite scroll, MutationObserver, notifications), which
 * meant anything importing it - including gear-core.js - could not be loaded in
 * Node, breaking the rule that -core modules are usable by the simulator.
 *
 * crafting.js re-exports getRecipeById from here, so existing importers and
 * tests are unaffected.
 */

import * as config from '../constants.js';
import { findItem } from './util.js';
import { applyCraftingCostScaling } from './raidUtil.js';
import { scaleRecipeInputs } from '../data/progressionScaling.js';

/**
 * Apply progression scaling to a recipe's inputs, by each input's own rarity.
 * @param {Object} recipe
 * @returns {Object} recipe with scaled inputs
 */
export function getScaledRecipe(recipe) {
  if (!recipe || !recipe.inputs) {
    return recipe;
  }

  const getRarityOfInput = (itemId) => {
    const item = findItem('id', itemId);
    return item?.rarity || 'common';
  };

  return {
    ...recipe,
    inputs: scaleRecipeInputs(recipe.inputs, getRarityOfInput)
  };
}

/**
 * Get a recipe by target item ID, with scaling applied.
 * @param {string} itemId - the ID of the target item
 * @returns {Object|null} the scaled recipe, or null if there is none
 */
export function getRecipeById(itemId) {
  const recipe =
    config.recipesUncommon.find(r => r.target === itemId) ||
    config.recipesRare.find(r => r.target === itemId) ||
    config.recipesEpic.find(r => r.target === itemId) ||
    config.recipesArtifact.find(r => r.target === itemId);

  return recipe ? getScaledRecipe(recipe) : null;
}

/**
 * Does the character hold every input this (already scaled) recipe needs?
 *
 * Moved here from crafting.js when Auto-Fabricator needed it: the fabricator
 * must know a craft will succeed BEFORE calling it, because every failure path
 * in crafting.js raises a player-facing notification, and an automation that
 * probes recipes would spam the log with things the player never asked for.
 * crafting.js re-exports it, so its own callers are unchanged.
 *
 * Note it does NOT check credits. Component crafting charges them and artifact
 * crafting does not, so the caller that knows which one it is checks that.
 */
export function canCraftRecipe(scaledRecipe, character) {
  if (!scaledRecipe?.inputs) return false;
  const inventory = character?.inventory || {};
  return scaledRecipe.inputs.every(input => (inventory[input.id] || 0) >= input.qty);
}

/**
 * Credits one craft of `itemId` costs, before bulk discounts.
 *
 * Extracted from `calculateBulkCraftingCost` (which now calls this) when
 * Auto-Fabricator needed to know the price before committing to a craft. One
 * source of truth on purpose: an automation that priced crafts differently from
 * the crafting screen would spend the player's credits at a rate they never
 * agreed to.
 */
export function craftCreditCost(itemId) {
  const item = findItem('id', itemId);
  if (!item) return 0;
  return applyCraftingCostScaling(config.itemRarities[item.rarity]?.craftingCost || 10, item.rarity);
}
